import { useEffect, useState } from 'react';
import { CollapseProps } from 'antd';
import { FormikProps } from 'formik';

import { Input, MultiSelect, Select } from '@Components/Filters';
import { MFilter } from '@Components/Base';
import { useAppSelector } from '@Hooks/index';
import { ItRequisitionTypeCodes } from '@Enum/api/ItRequisitionTypeCodes.enum';
import {
  getITAccessoryOptions,
  getOrganizationOptions,
} from '@Store/selectors/dictionarySelector';
import { useGetProjectsForOrgMutation } from '@Store/api/dictionaryApiSlice';
import { transformProjectsToLabelOptions } from '@Utils/transformToOptions';
import { Option } from '@Components/types/options';

import { FilterForm } from './types';
import {
  getItRequisitionTypeOptions,
  getSimCapLabels,
  getStatusLabels,
} from '../store/selectors/itRequisitionSelector';
import { userLabels } from '../isNewUser.enum';
import {
  accountModuleOptions,
  ModuleOptions,
  moduleOptions,
  requiredRoleLabels,
} from '../fieldsOptions';

interface FiltersProps {
  formik: FormikProps<FilterForm>;
  showError?: boolean;
}

const Filters: React.FC<FiltersProps> = ({ formik, showError = false }) => {
  const itemTypes = useAppSelector(getItRequisitionTypeOptions);
  const statuses = useAppSelector(getStatusLabels);
  const simCapLabels = useAppSelector(getSimCapLabels);
  const accessories = useAppSelector(getITAccessoryOptions);
  const organizations = useAppSelector(getOrganizationOptions);

  const [projects, setProjects] = useState<Option[]>([]);

  const [getProjects] = useGetProjectsForOrgMutation();

  useEffect(() => {
    const { organization } = formik.values;

    if (organization) {
      getProjects(organization)
        .unwrap()
        .then((projectsResp) => {
          const projectOptions = transformProjectsToLabelOptions(projectsResp);
          setProjects(projectOptions);
        })
        .catch(() => {
          setProjects([]);
        });
    }
  }, [formik.values.organization]);

  const items: CollapseProps['items'] = [
    {
      id: 'ITEM TYPE',
      label: 'Item Type',
      children: (
        <Select<FilterForm>
          formik={formik}
          formikKey="itemType"
          options={itemTypes}
        />
      ),
    },
  ];

  if (formik.values.itemType === ItRequisitionTypeCodes.NEW_SIM_CARD) {
    items.push(
      ...[
        {
          id: 'SIM CARD CREDIT LIMIT',
          label: 'SIM Card Credit Limit',
          children: (
            <MultiSelect<FilterForm>
              formik={formik}
              formikKey="simCardCreditLimit"
              options={simCapLabels}
            />
          ),
        },
        {
          id: 'CONTACT NUMBER',
          label: 'Contact Number',
          children: (
            <Input<FilterForm> formik={formik} formikKey="contactNumber" />
          ),
        },
      ]
    );
  }

  if (formik.values.itemType === ItRequisitionTypeCodes.EMAIL) {
    items.push(
      ...[
        {
          id: 'EMAIL ID',
          label: 'Email ID',
          children: <Input<FilterForm> formik={formik} formikKey="emailId" />,
        },
        {
          id: 'EMAIL SIGNATURE',
          label: 'Email Signature',
          children: (
            <Input<FilterForm> formik={formik} formikKey="emailSignature" />
          ),
        },
        {
          id: 'CONTACT NUMBER',
          label: 'Contact Number',
          children: (
            <Input<FilterForm> formik={formik} formikKey="contactNumber" />
          ),
        },
      ]
    );
  }

  if (formik.values.itemType === ItRequisitionTypeCodes.ACCESSORIES) {
    items.push(
      ...[
        {
          id: 'ACCESSORIES',
          label: 'Accessories',
          children: (
            <Select<FilterForm>
              formik={formik}
              formikKey="accessory"
              options={accessories}
            />
          ),
        },
        {
          id: 'CONTACT NUMBER',
          label: 'Contact Number',
          children: (
            <Input<FilterForm> formik={formik} formikKey="contactNumber" />
          ),
        },
      ]
    );
  }

  if (formik.values.itemType === ItRequisitionTypeCodes.SIM_ADDON_REQUEST) {
    items.push(
      ...[
        {
          id: 'MOBILE',
          label: 'Mobile',
          children: (
            <Input<FilterForm> formik={formik} formikKey="contactNumber" />
          ),
        },
      ]
    );
  }

  if (formik.values.itemType === ItRequisitionTypeCodes.LOST_SIM_CARD) {
    items.push(
      ...[
        {
          id: 'MOBILE',
          label: 'Mobile',
          children: (
            <Input<FilterForm> formik={formik} formikKey="contactNumber" />
          ),
        },
      ]
    );
  }

  if (formik.values.itemType === ItRequisitionTypeCodes.ASSET_STATUS_CHANGE) {
    items.push(
      ...[
        {
          id: 'CONTACT NUMBER',
          label: 'Contact Number',
          children: (
            <Input<FilterForm> formik={formik} formikKey="contactNumber" />
          ),
        },
      ]
    );
  }

  if (formik.values.itemType === ItRequisitionTypeCodes.INTERNATIONAL_CALLS) {
    items.push(
      ...[
        {
          id: 'INTERNATIONAL NUMBER',
          label: 'International Number',
          children: (
            <Input<FilterForm> formik={formik} formikKey="contactNumber" />
          ),
        },
      ]
    );
  }

  if (formik.values.itemType === ItRequisitionTypeCodes.BUILD_SMART_ACCESS) {
    const showAccountsModule =
      formik.values.module === String(ModuleOptions.ACCOUNTS);
    const showRequiredRole =
      formik.values.module === String(ModuleOptions.PURCHASE);

    const extraItems = [
      {
        id: 'USER INFORMATION',
        label: 'User Information',
        children: (
          <MultiSelect<FilterForm>
            formik={formik}
            formikKey="userInformation"
            options={userLabels}
          />
        ),
      },
      {
        id: 'MODULE',
        label: 'Module',
        children: (
          <Select<FilterForm>
            formik={formik}
            formikKey="module"
            options={moduleOptions}
          />
        ),
      },
      showAccountsModule
        ? {
            id: 'ACCOUNTS MODULE',
            label: 'Accounts Module',
            children: (
              <Select<FilterForm>
                formik={formik}
                formikKey="accountsModule"
                options={accountModuleOptions}
              />
            ),
          }
        : null,
      {
        id: 'ORGANIZATION',
        label: 'Organization',
        children: (
          <Select<FilterForm>
            formik={formik}
            formikKey="organization"
            options={organizations}
          />
        ),
      },
      {
        id: 'PROJECT',
        label: 'Project',
        children: (
          <Select<FilterForm>
            formik={formik}
            formikKey="project"
            options={projects}
          />
        ),
      },
      showRequiredRole
        ? {
            id: 'REQUIRED ROLE',
            label: 'Required Role',
            children: (
              <MultiSelect<FilterForm>
                formik={formik}
                formikKey="requiredRoles"
                options={requiredRoleLabels}
              />
            ),
          }
        : null,
      {
        id: 'CONTACT NUMBER',
        label: 'Contact Number',
        children: (
          <Input<FilterForm> formik={formik} formikKey="contactNumber" />
        ),
      },
    ].filter((item) => item) as CollapseProps['items'];

    items.push(...extraItems!);
  }

  items.push({
    id: 'STATUS',
    label: 'Status',
    children: (
      <MultiSelect<FilterForm>
        formik={formik}
        formikKey="statuses"
        options={statuses}
        showSearch
      />
    ),
  });

  return (
    <MFilter<FilterForm> showError={showError} formik={formik} items={items} />
  );
};

export default Filters;
